import Colors from '@constants/Colors';
import { InfoOutlined } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { useFormContext } from 'react-hook-form';

const sectionMap = [
    { key: 'title', label: "Titre de l'offre" },
    { key: 'missions', label: 'Missions' },
    { key: 'qualifications', label: 'Profil recherché' },
    { key: 'contract', label: 'Contrat' },
    { key: 'salary', label: 'Salaire' },
    { key: 'recruitment_process', label: 'Process de recrutement' },
    { key: 'advantages', label: 'Avantages et Atouts' },
];

const TOTAL_SECTIONS = sectionMap.length;

const hasText = (value?: string): boolean => Boolean(value && value.trim().length > 0);

const computeClientCompletedSections = (values: any): Set<string> => {
    const sections = new Set<string>();

    if (hasText(values?.title)) sections.add('title');
    if (hasText(values?.description?.missions)) sections.add('missions');

    const specialties = values?.candidate?.specialties;
    const softSkills = values?.candidate?.softSkills;
    const softSkillIds = values?.candidate?.softSkillIds;
    const degree = values?.candidate?.degree;
    const languages = values?.candidate?.languages;
    const hasQualifications =
        (Array.isArray(specialties) && specialties.length > 0) ||
        (Array.isArray(softSkills) && softSkills.length > 0) ||
        (Array.isArray(softSkillIds) && softSkillIds.length > 0) ||
        (degree != null && degree !== '') ||
        (Array.isArray(languages) && languages.length > 0);
    if (hasQualifications) sections.add('qualifications');

    if (hasText(values?.contract?.contractType)) sections.add('contract');

    const remunerationMin = values?.contract?.remuneration?.min;
    const remunerationMax = values?.contract?.remuneration?.max;
    if (typeof remunerationMin === 'number' || typeof remunerationMax === 'number') sections.add('salary');

    const { place, quickResponse, mode, attempt } = values?.recruitment ?? {};
    if (place || quickResponse || mode || attempt) sections.add('recruitment_process');

    const advantages = values?.description?.advantages;
    const listAdvantages = values?.description?.listAdvantages;
    if (hasText(advantages) || (Array.isArray(listAdvantages) && listAdvantages.length > 0)) sections.add('advantages');

    return sections;
};

const CompletionContainer = styled(Box)(() => ({
    backgroundColor: Colors.DASHBOARD,
    borderRadius: '16px',
    padding: '24px 16px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
}));

const ProgressBarContainer = styled(Box)(() => ({
    backgroundColor: Colors.DISABLE_FORM_BLACK,
    height: '16px',
    borderRadius: '12px',
    position: 'relative',
    overflow: 'hidden'
}));

const ProgressFill = styled(Box)<{ progress: number }>(({ progress }) => ({
    backgroundColor: Colors.SUCCESS_GREEN,
    height: '16px',
    borderRadius: '8px',
    width: `${progress}%`,
    position: 'absolute',
    left: 0,
    top: 0,
    transition: 'width 0.3s ease',
    zIndex: 2,
}));

const ProgressFillGhost = styled(Box)<{ progress: number }>(({ progress }) => ({
    backgroundColor: Colors.SUCCESS_GREEN,
    opacity: 0.35,
    height: '16px',
    borderRadius: '8px',
    width: `${progress}%`,
    position: 'absolute',
    left: 0,
    top: 0,
    transition: 'width 0.3s ease',
    zIndex: 1,
}));

const ProgressText = styled(Typography)(() => ({
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontWeight: 500,
    fontSize: '12px',
    color: Colors.PRIMARY_PURPLE_30,
    textAlign: 'center',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 10
}));

const SectionItem = styled(Box)<{ completed?: boolean }>(({ completed }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%'
}));

const SectionText = styled(Typography)<{ completed?: boolean }>(({ completed }) => ({
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontWeight: 600,
    fontSize: '14px',
    color: completed ? Colors.SUCCESS_GREEN : Colors.WARNING_ORANGE,
    textDecoration: completed ? 'line-through' : 'none',
    textDecorationSkipInk: 'none'
}));

const IconContainer = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px'
}));

interface JobOfferCompletionDetailProps {
    pro: any;
    jobOffer: {
        profileCompletionRate?: number;
        completedProfileSections?: string[];
    };
    onSubmit?: () => Promise<void>;
}

const JobOfferCompletionDetail = ({ jobOffer }: JobOfferCompletionDetailProps) => {
    const { watch } = useFormContext();
    const formValues = watch();

    const savedCompletionValue = Math.min(100, Math.max(0, Math.round(jobOffer?.profileCompletionRate ?? 0)));
    const savedSectionSet = new Set(jobOffer?.completedProfileSections ?? []);

    const clientSectionSet = computeClientCompletedSections(formValues);
    const clientCompletionValue = Math.round((clientSectionSet.size / TOTAL_SECTIONS) * 100);

    if (savedCompletionValue === 100 && clientCompletionValue === 100) {
        return null;
    }

    return (
        <CompletionContainer>
            {/* Header Section */}
            <Box display="flex" flexDirection="column" gap="4px">
                <Typography
                    variant="h6"
                    sx={{
                        fontSize: '24px',
                        fontWeight: 500,
                        lineHeight: 'normal'
                    }}
                >
                    Complétez votre offre d'emploi
                </Typography>
                <Typography
                    sx={{
                        fontFamily: 'Plus Jakarta Sans, sans-serif',
                        fontWeight: 400,
                        fontSize: '14px',
                        color: Colors.NEUTRAL_LIGHT_GRAY
                    }}
                >
                    Complétez votre offre et multipliez votre visibilité auprès des candidats
                </Typography>
            </Box>

            {/* Progress Bar */}
            <ProgressBarContainer>
                {/* Ghost bar: client-side preview (unsaved) */}
                {clientCompletionValue > savedCompletionValue && (
                    <ProgressFillGhost progress={clientCompletionValue} />
                )}
                {/* Solid bar: saved to server */}
                <ProgressFill progress={savedCompletionValue} />
                <ProgressText>
                    {savedCompletionValue}%
                </ProgressText>
            </ProgressBarContainer>

            {/* Sections List */}
            <Box display="flex" flexDirection="column" gap="12px">
                {sectionMap.map((section) => {
                    const savedCompleted = savedSectionSet.has(section.key);
                    const clientCompleted = clientSectionSet.has(section.key);
                    const pendingSave = !savedCompleted && clientCompleted;
                    return (
                        <SectionItem key={section.key} completed={savedCompleted}>
                            <SectionText completed={savedCompleted}>
                                {section.label}
                            </SectionText>
                            <IconContainer>
                                {savedCompleted ? (
                                    <CheckCircleIcon
                                        sx={{
                                            color: Colors.SUCCESS_GREEN,
                                            fontSize: '20px'
                                        }}
                                    />
                                ) : pendingSave ? (
                                    <CheckCircleOutlineIcon
                                        sx={{
                                            color: Colors.SUCCESS_GREEN,
                                            fontSize: '20px',
                                            opacity: 0.45,
                                        }}
                                    />
                                ) : (
                                    <InfoOutlined
                                        sx={{
                                            color: Colors.WARNING_ORANGE,
                                            fontSize: '20px'
                                        }}
                                    />
                                )}
                            </IconContainer>
                        </SectionItem>
                    );
                })}
            </Box>
        </CompletionContainer>
    );
};

export default JobOfferCompletionDetail;

